import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Filter, Plus, ArrowRight, LayoutGrid, List } from "lucide-react";
import { KanbanBoard } from "@/components/deal-pipeline/KanbanBoard";
import { DealDocumentsTable } from "@/components/deal-pipeline/DealDocumentsTable";
import { useDealPipeline } from "@/hooks/useDealPipeline";

// Couleurs alignées sur les 11 vraies étapes (voir PIPELINE_STAGES dans
// useDealPipeline.ts, la source de vérité des labels). Ce tableau local
// ne garde que le mapping couleur, propre à cette page.
const STAGE_COLOR_MAP: Record<string, string> = {
  submission: "bg-slate-500",
  pre_evaluation: "bg-blue-500",
  cim_creation: "bg-indigo-500",
  payment_pending: "bg-purple-500",
  published: "bg-pink-500",
  lender_submission: "bg-orange-500",
  nda_signed: "bg-amber-500",
  buyer_contact: "bg-yellow-500",
  first_meeting: "bg-lime-500",
  loi_signed: "bg-green-500",
  closing: "bg-emerald-500",
};

const PIPELINE_STAGES = [
  { key: "submission", label: "Submission" },
  { key: "pre_evaluation", label: "Pre-Evaluation" },
  { key: "cim_creation", label: "CIM Creation" },
  { key: "payment_pending", label: "Payment Pending" },
  { key: "published", label: "Published" },
  { key: "lender_submission", label: "Lender Submission" },
  { key: "nda_signed", label: "NDA Signed" },
  { key: "buyer_contact", label: "Buyer Contact" },
  { key: "first_meeting", label: "First Meeting" },
  { key: "loi_signed", label: "LOI Signed" },
  { key: "closing", label: "Closing" },
].map(s => ({ ...s, color: STAGE_COLOR_MAP[s.key] }));

interface Deal {
  id: string;
  business_name: string;
  current_stage: string;
  asking_price?: number;
  currency?: string;
  reference_number?: string;
  created_at: string;
}

export default function DealFlow() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"list" | "kanban">("list");
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const { fetchDeals } = useDealPipeline();

  useEffect(() => {
    fetchDeals().then((data) => {
      setDeals(data || []);
      setLoading(false);
    });
  }, []);

  // Filter deals
  const filteredDeals = deals.filter((deal) => {
    const matchesSearch =
      deal.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      deal.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || deal.current_stage === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Group deals by stage for summary
  const dealsByStage = PIPELINE_STAGES.map((stage) => ({
    ...stage,
    count: deals.filter((d) => d.current_stage === stage.key).length,
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Deal Flow</h1>
          <p className="text-white/60 mt-1">
            Manage and track deals through the pipeline
          </p>
        </div>
        <Button className="bg-brand-gold text-brand-navy hover:bg-brand-gold/90">
          <Plus className="h-4 w-4 mr-2" />
          New Deal
        </Button>
      </div>

      {/* Pipeline Summary */}
      <Card className="bg-white/10 border-white/20">
        <CardHeader>
          <CardTitle className="text-white">Pipeline Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {dealsByStage.map((stage) => (
              <button
                key={stage.key}
                onClick={() => setStatusFilter(stage.key === statusFilter ? "all" : stage.key)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  statusFilter === stage.key
                    ? "bg-white/20 ring-2 ring-brand-gold"
                    : "bg-white/5 hover:bg-white/10"
                }`}
              >
                <div className={`h-2 w-2 rounded-full ${stage.color}`} />
                <span className="text-sm text-white/80">{stage.label}</span>
                <Badge variant="secondary" className="bg-white/10 text-white">
                  {stage.count}
                </Badge>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            placeholder="Search deals..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-white/10 border-white/20 text-white">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {PIPELINE_STAGES.map((stage) => (
              <SelectItem key={stage.key} value={stage.key}>
                {stage.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Button
            variant={viewMode === "list" ? "default" : "outline"}
            size="icon"
            onClick={() => setViewMode("list")}
            className={viewMode === "list" ? "bg-brand-gold text-brand-navy" : ""}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === "kanban" ? "default" : "outline"}
            size="icon"
            onClick={() => setViewMode("kanban")}
            className={viewMode === "kanban" ? "bg-brand-gold text-brand-navy" : ""}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Deals View */}
      {viewMode === "list" ? (
        <Card className="bg-white/10 border-white/20">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-3 text-sm font-medium text-white/60">
                      Ref #
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-white/60">
                      Deal
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-white/60">
                      Stage
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-white/60">
                      Price
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-white/60">
                      Created
                    </th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-white/60">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                     <td colSpan={6} className="px-4 py-8 text-center text-white/40">
                        Loading deals...
                      </td>
                    </tr>
                  ) : filteredDeals.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-white/40">
                        No deals found
                      </td>
                    </tr>
                  ) : (
                    filteredDeals.map((deal) => {
                      const stage = PIPELINE_STAGES.find((s) => s.key === deal.current_stage);
                      return (
                        <tr
                          key={deal.id}
                          className="border-b border-white/5 hover:bg-white/5 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <span className="text-xs font-mono text-brand-gold">
                              {deal.reference_number || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-white">{deal.business_name}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              variant="outline"
                              className={`${stage?.color} bg-opacity-20 text-white border-transparent`}
                            >
                              {stage?.label || deal.current_stage}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-white">
                            {deal.asking_price
                              ? `${deal.currency || "$"}${deal.asking_price.toLocaleString()}`
                              : "—"}
                          </td>
                          <td className="px-4 py-3 text-white/60">
                            {new Date(deal.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button variant="ghost" size="sm" className="text-white/60 hover:text-white">
                              View
                              <ArrowRight className="h-4 w-4 ml-1" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="text-white/60 text-center py-8">Kanban view - Coming soon</div>
      )}

      {/* Documents Table */}
      <DealDocumentsTable />
    </div>
  );
}
